import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import * as categoryRetrievers from 'data/categories/retrievers';
import { List } from 'immutable';

import { isLoanAccountFromId } from 'data/accounts/utils';
import { ensureISODate } from 'utils/utils';
import { updateNewTags } from 'data/tags/utils';
import { coaIsBalanceAdjustmentType } from 'data/chartOfAccounts/utils';
import { performTransactionAction } from 'data/transactions/actions';
import { CashFlowTransaction } from 'data/transactions/types';

import { isAcme } from 'isAcme';

import { getLogger } from 'utils/logger';
import { getAccountString } from '../accounts/retrievers';

import { splitTxnRemainder, addRemainderToSplit } from './splitsHelpers';

import {
  isTransferTxn,
  setReportsFlags,
  isMultiCurrencyTransferTxn,
  isSplitTxn,
  isUnsyncedTransferTxn,
  isSupportedTransferTxn,
  isLoanAcctTransferTxn,
  isInvestmentAcctTransferTxn,
  isUnacceptedScheduledTxn,
  isPendingTxn,
  isBankDownloadedTxn,
  addPossibleMatch,
  findTransaction,
  findMatchingTransferTx,
  findPossibleTransferCandidate,
  possiblyMatchTransfer,
  getTxnDifferences,
  fieldChanged,
  areTagsEqual,
  transactionEditsNotAllowed,
  nextScheduledInstanceAfterTransaction,
} from './utils';

const log = getLogger('data/transactions/preProcess.js');


export function txnCategorySign(txn) {

  if (isSplitTxn(txn)) {
    return 1;
  }
  const isIncomeCat = txn.coa ? categoryRetrievers.isIncomeCat(txn.coa.id) : false;

  return isIncomeCat ? 1 : -1;
}

function makeTransferTxn(txn, guid, overrides) {

  return new CashFlowTransaction({
    accountId: overrides?.accountId || txn.coa.id,
    id: '0',
    coa: {
      type: 'ACCOUNT',
      id: txn.accountId,
    },
    amount: overrides?.amount || String(Number(txn.amount) * -1),
    split: null,
    cpData: null,
    matchState: 'NOT_MATCHED',
    transfer: { clientId: txn.clientId }, // assumes clientId already set
    clientId: guid,  // new GUID for transfer transaction
    tags: overrides?.tags || txn.tags,
    isExcludedFromReports: true,  // by default both sides of a transfer are excluded from reports
    isExcludedFromF2S: true,  // by default both sides of a transfer are excluded from reports
    check: null,

    payee: txn.payee,
    memo: txn.memo,
    postedOn: txn.postedOn,
    postedOnAsUnix: txn.postedOnAsUnix,
    state: txn.state,
    stModelId: txn.stModelId,
    stDueOn: txn.stDueOn,
    isBill: txn.isBill,
    isSubscription: txn.isSubscription,

  });
}


// ====================================================
// createRequiredTransferTxns
//
// If this is a transfer transaction (or has split children which are transfers),
// we will make the other side(s) of it.
//
export function createRequiredTransferTxns(txnsToCreate, isUpdate, txnsByAccountId, forceWarnOnMatch, state) {

  const allTxnsToCreate = [];
  const allTxnsToUpdate = [];
  const warnings = [];
  const allMatches = [];
  const extraActions = [];

  txnsToCreate.forEach((x) => {

    let baseTxn = x;

    if (baseTxn.isDeleted) {  // if the baseTxn is being deleted, don't do anything
      createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);
    } else if (isSplitTxn(baseTxn)) {

      // process splits
      // baseTxn represents the txn we are processing, as a split, we will need to alter any
      // transfer split items to contain the GUID connectors

      baseTxn.split.items.forEach((item, index) => {
        if (isSupportedTransferTxn(item, state) &&
          item.coa.id !== baseTxn.accountId) {

          const newGuid = uuidv4().toUpperCase();  // new GUID for transfer transaction

          // check for a matching transaction, if found save with GUID for later syncing
          const possibleMatch = findPossibleTransferCandidate(item.coa.id,
            baseTxn.postedOn,
            String(-1 * Number(item.amount)),
            txnsByAccountId);
          // log.log('1. POSSIBLE MATCH IS ===> ', possibleMatch);

          const transferTxn = makeTransferTxn(baseTxn, newGuid, {
            accountId: item.coa.id,
            amount: item.amount * -1,
            tags: item.tags,
          });
          /*
          const transferTxn = baseTxn.merge({
            accountId: item.coa.id,
            id: '0',
            coa: {
              type: 'ACCOUNT',
              id: baseTxn.accountId,
            },
            amount: item.amount * -1,
            split: null,
            cpData: null,
            matchState: 'NOT_MATCHED',
            transfer: { clientId: baseTxn.clientId }, // assumes clientId already set
            clientId: newGuid,
            tags: item.tags,
            isExcludedFromReports: true,  // by default both sides of a transfer are excluded from reports
            isExcludedFromF2S: true,  // by default both sides of a transfer are excluded from reports
          });
          */
          // by default both sides of a transfer are excluded from reports
          baseTxn = setReportsFlags(baseTxn, true);

          // set the guid on both the baseTxn, and the split item
          baseTxn = baseTxn.setIn(['split', 'items', index, 'transfer'], { clientId: newGuid });
          // push the other side of this split transfer
          allTxnsToCreate.push(transferTxn);

          // If we have a possible match, save it and save the matching transfer "to create" for processing
          if (possibleMatch) {
            addPossibleMatch(possibleMatch, baseTxn, transferTxn.set('accountId', baseTxn.accountId), allMatches, forceWarnOnMatch);
          } else if (isAcme) {
            warnings.push(`We did not find a matching transaction in the account ${getAccountString(transferTxn.accountId)}.` +
              '  Click continue to create a manual transaction, or Cancel to abort');
          }

        }
      });
      // push the base transaction
      createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);

    } else if (isSupportedTransferTxn(baseTxn, state)) {

      // if not a self transfer, then create a matching transfer transaction
      // force it to be a MANUAL transaction, and set Match State
      if (baseTxn.coa.id !== baseTxn.accountId) {

        // check to see if there is a transaction already in the transfer account that might be
        // a target to match up with this transfer.
        const possibleMatch = findPossibleTransferCandidate(baseTxn.coa.id,
          baseTxn.postedOn,
          -1 * baseTxn.amount,
          txnsByAccountId);

        const newGuid = uuidv4().toUpperCase(); // New GUID for new transfer, save with match if any for later sync

        const transferTxn = makeTransferTxn(baseTxn, newGuid);

        /*
        const transferTxn = baseTxn.merge({
          accountId: baseTxn.coa.id,
          clientId: newGuid,
          id: '0',
          coa: {
            type: 'ACCOUNT',
            id: baseTxn.accountId,
          },
          amount: String(Number(baseTxn.amount) * -1),
          transfer: {
            clientId: baseTxn.clientId,
          },
          cpData: null,
          matchState: 'NOT_MATCHED',
          tags: baseTxn.tags,
          check: null,         // we do NOT copy the check number to the other side
          isExcludedFromReports: true,  // by default both sides of a transfer are excluded from reports
          isExcludedFromF2S: true,  // by default both sides of a transfer are excluded from reports
        });

         */
        baseTxn = setReportsFlags(baseTxn, true);

        allTxnsToCreate.push(transferTxn);

        // If we have a possible match, save it and save the matching transfer "to create" for processing
        if (possibleMatch) {
          addPossibleMatch(possibleMatch, baseTxn, transferTxn.set('accountId', baseTxn.accountId), allMatches, forceWarnOnMatch);
        } else if (isAcme) {
          warnings.push(`We did not find a matching transaction in the account ${getAccountString(transferTxn.accountId)}.` +
            '  Click continue to create a manual transaction, or Cancel to abort');
        }

        baseTxn = baseTxn.set('transfer', { clientId: newGuid });
        createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);

      } else { // handle the self transfer
        createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);
      }
      // if transaction is split, check line items for transfer items
    } else { // item is not split, not a transfer, just keep it as is
      // push the base transaction
      createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);
    }
  });
  return { allTxnsToUpdate, allTxnsToCreate, allMatches, warnings, extraActions };
}

// ----------------------------------------------------
// CreateOrUpdate (txn, createArray, updateArray)
function createOrUpdate(txn, ca, ua) {
  if (Number(txn.id) === 0) {
    ca.push(txn);
  } else {
    ua.push(txn);
  }
}

// ----------------------------------------------------
// removeTransfers
//
function mergeTxnPack(txnPack, packObj) {

  const { allMatches, allTxnsToCreate, allTxnsToUpdate, allTxnsToDelete, extraActions, warnings, errors } = packObj;

  if (txnPack.allTxnsToCreate) {
    txnPack.allTxnsToCreate.forEach((x) => {
      allTxnsToCreate.push(x);
    });
  }
  if (txnPack.allTxnsToUpdate) {
    txnPack.allTxnsToUpdate.forEach((x) => {
      allTxnsToUpdate.push(x);
    });
  }
  if (txnPack.allTxnsToDelete) {
    txnPack.allTxnsToDelete.forEach((x) => {
      allTxnsToDelete.push(x);
    });
  }
  if (txnPack.warnings) {
    txnPack.warnings.forEach((x) => {
      warnings.push(x);
    });
  }
  if (txnPack.errors) {
    txnPack.errors.forEach((x) => {
      errors.push(x);
    });
  }
  if (txnPack.allMatches) {
    txnPack.allMatches.forEach((x) => {
      allMatches.push(x);
    });
  }
  if (txnPack.extraActions) {
    txnPack.extraActions.forEach((x) => {
      extraActions.push(x);
    });
  }

}

// TODO for all of these we need to handle MATHED/UNMATCHED and DOWNLOADED cases and do the right thing
// whatever that is
//
// ====================================================
// preProcessDelete
//
export function preProcessDelete(options) {

  const { txns, txnsByAccountId, isUpdate = false, skipWarnings, state, featureFlags } = options;

  const allTxnsToDelete = [];
  const allTxnsToUpdate = [];
  const warnings = [];
  const errors = [];
  const extraActions = [];

  txns.forEach((txn) => {


    // if support for transfers is disabled, stop here
    if (featureFlags.get('disableTransfers') && isTransferTxn(txn)) {
      errors.push('Sorry!  We are really close to full support for transfers, and will be allowing ' +
        'deletion of transactions with transfers very shortly with an update.  We are talking weeks, not months!');
    }
    // we can use this to create deletion side effects without deleting the original transaction
    if (!isUpdate) {
      allTxnsToDelete.push(txn.set('isDeleted', true));
      if (txn.cpData) { // TODO need to check preference for 'editAmounts', maybe bypass warning?
        // warnings.push(`The transaction "${txn.payee}" was downloaded from your financial institution.` +
        //  ' You will not be able to download it again if you continue this delete operation.  ');
      }
    }
    if (isInvestmentAcctTransferTxn(txn, state)) {
      // if this transaction is a transfer to an investment account, do not allow deletion
      errors.push('This transaction contains a transfer to an investment account, You may only delete ' +
        'this transaction from the desktop.');
    }
    if (isUnsyncedTransferTxn(txn, state)) {
      // if this transaction is a transfer to an unsynced account, warn on deletion
      warnings.push('This transaction contains a transfer to an unsynced account, deleting it may ' +
        'have adverse effects when you sync back to the desktop.  Recommend aborting this action.');
    }
    // TODO Investment and Loan transfer transactions, allow delete?
    //
    if (isTransferTxn(txn)) {

      if (isSplitTxn(txn)) {
        // find the split item whose amount matches the amount
        txn.split.items.forEach((item) => {
          // if split item is a valid transfer, and not a self-transfer
          if (item.coa && item.coa.type === 'ACCOUNT' && item.coa.id !== txn.accountId) {
            const transferTxns = getTransferTxns(txnsByAccountId, item.coa.id); // txnsByAccountId.get(item.coa.id).filter((x) => isTransferTxn(x));
            removeMatchingTransfer({
              txn,
              transferTxns,
              updateArray: allTxnsToUpdate,
              deleteArray: allTxnsToDelete,
              warnings,
              errors,
              altAmount: item.amount,
              skipWarnings,
              state,
            });
          }
        });
      } else {

        // not a split transaction, so simply delete the other side of this transaction
        const transferTxns = getTransferTxns(txnsByAccountId, txn.coa.id); // txnsByAccountId.get(txn.coa.id).filter((x) => isTransferTxn(x));
        removeMatchingTransfer({
          txn,
          transferTxns,
          updateArray: allTxnsToUpdate,
          deleteArray: allTxnsToDelete,
          warnings,
          errors,
          altAmount: null,
          skipWarnings,
          state,
        });
      }
    }

  });

  return ({ allTxnsToDelete, allTxnsToUpdate, warnings, errors, extraActions });
}

function getTransferTxns(txnsByAccountId, id) {

  const acctTxns = txnsByAccountId.get(id) || new List([]);
  const ret = acctTxns.filter((x) => isTransferTxn(x));
  return ret;
}

//-------------------------------------------------------------------------------
// When the other side of a transfer is in a split, we have to update that one split
// row (we make the category 'UNCATEGORIZED')  We don't bother to consolidate the
// uncategorized rows here, although that is a TODO - consolidate uncategorized items
//
// removeMatchingTransfer
//
function removeMatchingTransfer({
  txn,
  transferTxns,
  updateArray,
  deleteArray,
  warnings,
  errors: _errors,
  altAmount = null,
  skipWarnings,
  state,
}) {

  const bestMatchTxn = findMatchingTransferTx(txn, transferTxns, altAmount);

  if (bestMatchTxn) {
    if (bestMatchTxn.splitItem !== null) {  // transfer is in a split

      // update in the immutable the coa id and the coa type
      const t1 = bestMatchTxn.txn.setIn(['split', 'items', bestMatchTxn.splitItem, 'coa'], { id: '0', type: 'UNCATEGORIZED' });
      const t2 = t1.setIn(['split', 'items', bestMatchTxn.splitItem, 'transfer'], null);
      updateArray.push(t2);
      const acctName = getAccountString(t2.accountId, state);
      if (!skipWarnings) {
        warnings.push(`You are deleting the transfer transaction "${txn.payee}".  The other side of this transaction` +
          ` in account [${acctName}] is in a split transaction and its line item will be set to "Uncategorized"`);
      }
    } else if (bestMatchTxn.txn.cpData) {
      //
      // if the matched transaction is a downloaded and/or matched transaction, do not delete it,
      // but uncategorize it
      // Also unset the exclusion from reports
      //
      const t1 = setReportsFlags(bestMatchTxn.txn.set('coa', { id: 0, type: 'UNCATEGORIZED' }), false);
      updateArray.push(t1);
      const acctName = getAccountString(t1.accountId, state);

      if (!skipWarnings) {
        warnings.push(`You are deleting the transfer transaction "${txn.payee}".  The other side of this transfer ` +
          ` in account [${acctName}] is a bank downloaded transaction and will be set to "Uncategorized", but will not be deleted`);
      }
    } else {
      const acctName = getAccountString(bestMatchTxn.txn.accountId, state);

      if (!skipWarnings) {
        warnings.push(`You are deleting the transfer transaction "${txn.payee}".  This will also delete the other side` +
          ` of the transfer in account [${acctName}] `);
      }
      deleteArray.push(bestMatchTxn.txn.set('isDeleted', true));
    }
  }
}

// ====================================================
// preProcessUpdate
//
// Here are the rules, subject to change
//
// Premise: Transaction has a transfer category OR has a split with transfer category(ies)
// Problem: Determine updates that need to happen to the other side of the transfer
// Note: The other side of the transfer may be a transaction coa, or within a split
// More Notes: transaction can go from split to not split, or the other way around
//
// COMPLEX!!!!!!!
//
// RULES:
//
// NORMAL TRANSFER: If this is a generic transfer (was not split) tightly bound
//  - if the other side of this transfer is within a split, NO CHANGES are allowed (Mac rules).  Transaction is locked
//  - change to amount will change both sides of the transfer
//  - change to the date will change both sides of the transfer (date/amount coupling is strongest) -with warning
//  - change to payee will change both sides of the transfer
//  - change to the category (break the transfer) will delete the other side of the transfer - with warning
//  - transaction becomes split - then previous transfer is deleted, and new transfers are created if applicable
//  - all other changes will not impact the other side of the transfer
//
// SPLIT TRANSFER: This transaction had one or more transfers in it's split items
// - change to the date will change both sides of the transfer(s) - with warning
// - change to the payee will change all payee names linked to split transfer rows
// - change to a split row amount that is a transfer will impact the amount of the other side of the transfer
// - change to a split row category that will break the existing transfer will delete the other side
// - all other changes will not impact the other side of any split row transfers
// - if the transaction became NON SPLIT (was split, now it is not) all prior transfers are deleted and then
//   if applicable, new transfer transaction created (if non split version is a transfer) - with warning
//

export function preProcessUpdate(options) {  // state is for unit testing

  const { txns, txnsByAccountId, state, featureFlags, productName } = options;

  const allTxnsToDelete = []; // transfer transactions could be eliminated
  const allTxnsToUpdate = []; // all transactions might change
  const allTxnsToCreate = []; // new transfer transactions may be created
  const allMatches = [];
  const warnings = [];
  const errors = [];
  const extraActions = [];

  function localMergeTxnPack(packItems) {
    mergeTxnPack(packItems, {
      allTxnsToUpdate,
      allTxnsToDelete,
      allTxnsToCreate,
      allMatches,
      warnings,
      errors,
      extraActions,
    });
  }

  // Process every transaction in the update
  txns.forEach((x) => {

    // if this is a split txn, remove the tags if there are any
    //
    let txn = isSplitTxn(x) ? x.set('tags', null) : x;
    let txnPrev = null;
    const isNewTxn = (Number(txn.id) === 0);

    let errorThrown = false; // if an error is thrown, we do not add any entries to the update/create/delete

    // make sure there is a legit amount
    if (txn.amount === null) {
      txn = txn.set('amount', '0');
    }

    // convert date to an ISO String
    txn = txn.set('postedOn', ensureISODate(txn.postedOn));

    // if this is a split transaction, remove any zero items
    if (isSplitTxn(txn)) {

      txn = txn.setIn(['split', 'items'], txn.split.items.filter((item) => Number(item.amount) !== 0));

      assert(!txn.coa, 'You are saving a transaction with a COA and a SPLIT - Please tell TIM what you did');
      txn = txn.set('coa', null);
      //
      // ensure the transaction balances
      //
      const remainder = splitTxnRemainder(txn);
      if (remainder !== 0.00) {
        txn = addRemainderToSplit(txn, remainder);
      }
    }

    // if this is a CREATE (new) Transaction, add a clientId if there is not one already
    if (isNewTxn && txn.clientId === null) {
      txn = txn.set('clientId', uuidv4().toUpperCase());
    }
    // if this is a split transaction, assign clientId's to any split items that do not have any
    if (isSplitTxn(txn)) {
      txn = txn.setIn(['split', 'items'], txn.split.items.map((item) => {
        if (item.id) {
          return item;
        }
        return item.set('clientId', uuidv4().toUpperCase());
      }))
    }

    // If this transaction has tags, check for any tags with id 0, and either find an actual tag
    // with that 'name' and use it's ID, or remove it
    //

    txn = updateNewTags(txn, true, state);

    let dateChanged = false;
    let accountChanged = false;
    let coaChanged = false;
    let tagsChanged = false;

    // if this transaction is for a loan account, don't allow it
    if (featureFlags.get('hideLoanTransactions') && isLoanAccountFromId(txn.accountId, state)) {
      errors.push('You can only add, modify, or delete loan account transactions from desktop Quicken');
      errorThrown = true;
    }

    if (featureFlags.get('hideLoanTransactions') && isLoanAcctTransferTxn(txn, state)) {
      errors.push('This transaction contains a transfer to a loan account.  ' +
        'You can only add, modify, or delete loan account transactions from desktop Quicken');
      errorThrown = true;
    }

    // get the current stored transaction by id to identify changes in the update
    if (!isNewTxn) {
      const txnsForAccount = txnsByAccountId.get(txn.accountId);
      txnPrev = txnsForAccount && txnsForAccount.find((tx) => tx.id === txn.id);
      if (!txnPrev) {
        // we may have changed the accountId, so let's do a less efficient search of all accounts
        txnPrev = findTransaction(txnsByAccountId, txn.id);
        if (!txnPrev) {
          // throw an exception, the tx is not in the store
          /* eslint-disable no-throw-literal */
          log.error('Error, could not find previous transaction', txn, txns);
          errorThrown = true;
          // throw 'ERROR: Could  not find transaction to update in REDUX';
          errors.push('We are sorry, there seems to be a problem updating this transaction.  Please try again.' +
          `if this error persists, please contact support.  (ERR: tx404, txID=${txn.id})`);

          // so the rest of the processing does not crash.  This is in error state and will
          // not be sent to QCS, but will give the user the error message
          txnPrev = txn.set('id', uuidv4().toUpperCase());
        }
      }

      dateChanged = fieldChanged(txn, txnPrev, 'postedOn');
      accountChanged = txn.accountId !== txnPrev.accountId;
      coaChanged = fieldChanged(txn, txnPrev, 'coa');
      tagsChanged = fieldChanged(txn, txnPrev, 'tags');

      // if this is a transfer transaction to an unsynced account, we do not allow the update
      //
      // if (isUnsyncedTransferTxn(txnPrev, state)) {
      //   errors.push('This transaction contains a transfer to an account which was not synced to the cloud. ' +
      //   'We recommend you sync all your accounts to the cloud for best use across web, mobile, and desktop');
      //   errorThrown = true;
      // }

      // if we are unreconciling a transaction, give a warning
      if (txnPrev.state === 'RECONCILED' && txn.state !== 'RECONCILED') {
        warnings.push('You are changing a previously reconciled transaction to be not reconciled. ' +
          'Are you sure you want to do this?');
      }

      if (featureFlags.get('hideLoanTransactions') && isLoanAcctTransferTxn(txnPrev, state)) {
        errors.push('This transaction contains a transfer to a loan account.  ' +
          'You can only add, modify, or delete loan account transactions from desktop Quicken');
        errorThrown = true;
      }

      if (isUnacceptedScheduledTxn(txn)) {
        // do not allow changing the date to before today or after the next scheduled instance
        // companion only (for now)
        if (!isAcme) {
          if (dateChanged && moment(txn.postedOn).isBefore(moment())) {
            errors.push('You cannot change the date of a scheduled transaction to the past.');
            errorThrown = true;
          } else if (dateChanged) {
            // find the next instance based on the current saved date for this transaction
            const nextInstance = nextScheduledInstanceAfterTransaction(txnPrev, txnsForAccount);
            // now test if the new date is within the range
            if (nextInstance && moment(nextInstance.postedOn).isSameOrBefore(moment(txn.postedOn), 'day')) {
              errors.push('You cannot change the date of this scheduled instance to be after the next one in the series');
            }
          }
        }
      }
      // in acme do not allow them to change a cleared/reconciled bank downloaded trnasaction to pending
      if (isAcme && !isPendingTxn(txnPrev) && isPendingTxn(txn) && isBankDownloadedTxn(txn)) {
        errors.push("Sorry, but you cannot change a cleared bank downloaded transaction to 'Pending'");
        errorThrown = true;
      }
      // in quicken, do not allow editing of sched tx instances with transfers to unsynced accounts
      // except we will allow chaning the CLR status
      // TODO: needs unit test
      if (transactionEditsNotAllowed(txnPrev)) {
        const diffs = getTxnDifferences(txn, txnPrev);
        const onlyStateChanged = diffs.length === 1 && diffs[0] === 'state';
        if (!onlyStateChanged) {
          errors.push('This is a billpay transaction, or a scheduled transaction instance to an unsynced account, you can only edit this from desktop Quicken');
        }
      }
    }

    // at this point, we want to see if the user requested a transfer using the top level transfer category
    // then we will search all other bank accounts for a possible matching transaction, and then let the match
    // code do it's thing.  WE only do this if the edited the coa or it is a new transaction
    let forceWarnOnMatch = false;
    if (isNewTxn || coaChanged) {
      const txn2 = txn;
      txn = possiblyMatchTransfer(txn, txnsByAccountId);
      forceWarnOnMatch = (txn2 !== txn);
    }

    // FEATURE FLAGGED support for transfers
    if (featureFlags.get('disableTransfers')) {
      if (isTransferTxn(txn) || (txnPrev && isTransferTxn(txnPrev))) {
        errors.push('Sorry!  We are really close to full support for transfers, and will be allowing ' +
          'editing and creation of transfers very shortly with an update.  We are talking weeks, not months!');
        errorThrown = true;
      }
    }

    // should not be possible, but do not allow creation of transactions to unsynced accounts
    if (isNewTxn && isUnsyncedTransferTxn(txn, state) && errors.length === 0) {
      errors.push('You cannot save a transaction with a transfer to an account which has not been synced');
      errorThrown = true;
    }

    // If this is a transfer transaction, and the currency types of the accounts are different, do not
    // allow to create or edit
    //
    if (isMultiCurrencyTransferTxn(txn, state)) {
      errors.push('Multi currency transfers: ' +
        'We are sorry, but creating or editing multi currency transfers is not supported.');
      errorThrown = true;
    }

    // After amount is finally determined, check if it changed for an existing transaction
    const amountChanged = isNewTxn ? false : Number(txn.amount) !== Number(txnPrev.amount);

    // do not allow editing or creation of transfers to investment accounts
    // EXCEPT when benign fields are edited, however we don't deal with splits... UNLESS only the state field changed
    // THis is ONLY for QUICKEN, it is OPEN SEASON for ACME
    //
    if (!isAcme) {
      if (!isNewTxn &&
        ((isUnsyncedTransferTxn(txnPrev, state) || isInvestmentAcctTransferTxn(txnPrev, state)) && errors.length === 0) ||
        ((isUnsyncedTransferTxn(txn, state) || isInvestmentAcctTransferTxn(txn, state)) && errors.length === 0)
      ) {

        if (isNewTxn) {
          errors.push('We are sorry, but creation of transfers to investment accounts or unsynced accounts is not yet supported, please use the desktop for this');
          errorThrown = true;
        } else {
          const diffs = getTxnDifferences(txn, txnPrev);
          const onlyStateChanged = diffs.length === 1 && diffs[0] === 'state';
          if (dateChanged || amountChanged || accountChanged || coaChanged || !onlyStateChanged && (isSplitTxn(txn) || isSplitTxn(txnPrev))) {
            errors.push('We are sorry, but you can only edit the clr status for transfers to investment or unsynced accounts on the web, please use the desktop for this');
            errorThrown = true;
          }
        }
      }
      // QUICKEN ONLY (not Acme)
      // if editing an existing scheduled transaction, use the action API to change the date/amount
      // should not allow editing anything else but the date and amount
      // we should check for 'isNextScheduledInstance' here, but that requires the sched txn state, and not worth it
      //
      if (!isAcme && isUnacceptedScheduledTxn(txn) && (dateChanged || amountChanged)) {
        const scheduledTxnUpdateAction = performTransactionAction(
          {
            action: 'override',
            id: txn.id,
            overrideNextDueOn: moment(txn.postedOn).format('YYYY-MM-DD'),
            overrideNextAmount: parseFloat(txn.amount),
          }
        );
        extraActions.push({ action: scheduledTxnUpdateAction, delay: 1000 });
      }

    }

    if (isNewTxn && featureFlags.get('warnOnUncategorized') && txn.coa && txn.coa.type === 'UNCATEGORIZED') {
      warnings.push('You are saving the transaction without a category. ' +
       `${productName} works best for you when you use categories.`);
    }


    /* *******************************************************************************************
     * Begin Processing the transaction, below are various cases for processing the update/create
     * Start by ensuring there are not validation errors with the fields
     * If an error was thrown above, we just bail out and do not do the subsequent processing/checks
     */
    const validateError = validateTxn(txn);

    // convert the amount(s) to numbers
    txn = txn.set('amount', Number(txn.amount));
    if (isSplitTxn(txn)) {
      const newItems = txn.split.items.map((item) => item?.set('amount', Number(item.amount)));

      txn.set('split', txn.split.set('items'), newItems);
    }

    if (!txn.isDeleted && validateError || errorThrown) {
      if (!errorThrown) {
        errors.push(`Cannot ${isNewTxn ? 'create' : 'update'} transaction ${txn.payee || ''}: ${validateError}`);
        errorThrown = true;
      }

    /*
     * Do not allow changing the account of a downloaded transaction
     */
    } else if ((txn.cpData || isTransferTxn(txn)) && accountChanged) {
      errors.push(`You cannot change the account of a ${txn.cpData ? 'downloaded' : 'transfer'} transaction`);
      errorThrown = true;

    /*
     * NO TRANSFERS in the previous transaction, proceed with update/create
     * Note, the new transaction may have transfers, so we create those
     * THIS IS THE MOST COMMON CASE, previous transaction is being updated and had no transfers
     * OR IF txnPrev is null, there is no previous transaction (this is a create)
     */
    } else if (!txnPrev || !isTransferTxn(txnPrev)) {

      const setFlag = !txnPrev || coaChanged;
      // if the category for this transaction is the L1 category for transfers, we set ignore from reports to true

      if (setFlag) {
        if (coaIsBalanceAdjustmentType(txn.coa)) {
          txn = setReportsFlags(txn, true);
        } else if (txnPrev && coaIsBalanceAdjustmentType(txnPrev.coa)) {
          txn = setReportsFlags(txn, false);
        }
      }

      const txnPack = createRequiredTransferTxns([txn], !isNewTxn, txnsByAccountId, forceWarnOnMatch, state);
      localMergeTxnPack(txnPack);

    /*
     * REMAINING CASE ARE FOR PROCESSING THE PRIOR TRANSFER(S) IN THE PREVIOUS TRANSACTION
     */
    } else {

      // retrieve txn candidates for matching transfer items in the split
      const oldDestinationTransfers = getTransferCandidates(txnPrev, txnsByAccountId);

      // determine transfer endpoints that would be created by the new transaction, these will
      // be in txnPack.allTxnsToCreate
      let { allTxnsToCreate: newTransfers } = createRequiredTransferTxns([txn], true, txnsByAccountId, forceWarnOnMatch, state);
      //
      // for each old transfer endpoint, we see if it has a match in the new
      // transactions to be created.  If so, preserve it, and if not, we delete it
      // to be a match the coa's much match, and the amounts must match
      //

      // if the current transaction is no longer a transfer, unset the report flags as well
      if (!isTransferTxn(txn) && !coaIsBalanceAdjustmentType(txn.coa)) {
        txn = setReportsFlags(txn, false);
      }

      const transfersToDelete = [];
      const transfersToUpdate = [txn];
      let transfersToCreate = [];

      oldDestinationTransfers.forEach((oldTransferPoint) => {

        /*
         * if the destination endpoint is in a split, we cannot edit shared fields
         */
        if (oldTransferPoint.splitItem !== null) {
          if (amountChanged || dateChanged || coaChanged || tagsChanged) {
            errors.push(`Cannot update transaction "${txn.payee}", the other side of the transfer is in a split.` +
              ' To update it, edit the transfer transaction in the transfer account');
            errorThrown = true;
          }
          newTransfers = [];
        /*
         * Destination endpoint is not in a split, so we can make adjustments to transfers
         */
        } else {

          // find matching transfer items in the "to be created" list which match with the old transfer endpoints list
          // ensure accounts match
          // sort matches by difference of amount, so that it will try and find the correct one in the case of multiples
          const endpointMatches = newTransfers.filter((xfr) =>
            (xfr.accountId === oldTransferPoint.txn.accountId));
          endpointMatches.sort((a, b) =>
            Math.abs((a.amount - oldTransferPoint.txn.amount)) - Math.abs((b.amount - oldTransferPoint.txn.amount)));

          // If we have endpoint matches between the txn to be saved and the prior transaction, use it
          if (endpointMatches && endpointMatches.length > 0) {

            // get the index of the actual match based on amount and account match
            const matchIndex = newTransfers.findIndex((xfr) =>
              (xfr.amount === endpointMatches[0].amount) && (xfr.accountId === endpointMatches[0].accountId));


            if ((oldTransferPoint.txn.amount !== newTransfers[matchIndex].amount) ||
              !areTagsEqual(oldTransferPoint.txn.tags, newTransfers[matchIndex].tags) ||
              !moment(oldTransferPoint.txn.postedOn).isSame(moment(newTransfers[matchIndex].postedOn), 'day')) {
              // update the pre existing transfer transaction to the new amount and date if they changed
              let updateTxn = oldTransferPoint.txn;

              updateTxn = updateTxn.set('postedOn', newTransfers[matchIndex].postedOn);
              updateTxn = updateTxn.set('amount', newTransfers[matchIndex].amount);
              updateTxn = updateTxn.set('tags', newTransfers[matchIndex].tags);
              transfersToUpdate.push(updateTxn);

            }
            // remove this new transfer from the consideration and creation list (already used)
            newTransfers.splice(matchIndex, 1);
          } else if (oldTransferPoint.txn.cpData) {
            // if the endpoint is a bank downloaded transaction, uncategorize it, and unset reports exclusion
            transfersToUpdate.push(
              setReportsFlags(oldTransferPoint.txn.set('coa', { type: 'UNCATEGORIZED', id: '0' }), false)
            );
          } else {
            // we delete the old endpoint (no longer a reference to it in the base txn)
            transfersToDelete.push(oldTransferPoint.txn.set('isDeleted', true));
          }
        }
      });

      transfersToCreate = transfersToCreate.concat(newTransfers);
      // warnings.push('testing');

      if (!errorThrown) {
        localMergeTxnPack({
          allTxnsToUpdate: transfersToUpdate,
          allTxnsToDelete: transfersToDelete,
          allTxnsToCreate: transfersToCreate,
        });
      }
    }
  }); // for each txn

  //
  // in some cases during a multi-transaction update, a transfer transaction might be getting updated
  // along with one of it's children.  This can result in a new child transaction being created, and the
  // previous transaction being deleted, but also the previous transaction may attempt to update itself
  // effectively undeleting itself.  We check for any updates conflicting with deletes, and we remove
  // the updates if there is a conflict (delete wins)
  //
  const txnPack = { allMatches, allTxnsToDelete, allTxnsToUpdate, allTxnsToCreate, warnings, errors, extraActions };
  return removeConflicts(txnPack);

}

function removeConflicts(txnPack) {
  const { allTxnsToDelete, allTxnsToUpdate } = txnPack;

  const newUpdate = allTxnsToUpdate.filter((txn) => !(allTxnsToDelete.find((x) => x.id === txn.id)));
  return ({ ...txnPack, allTxnsToUpdate: newUpdate });
}

function getTransferCandidates(txnPrev, txnsByAccountId) {
  const oldDestinationTransfers = [];
  if (isSplitTxn(txnPrev)) {
    txnPrev.split.items.forEach((item) => {
      if (item.coa.type === 'ACCOUNT') {
        const txnsFromTransferAccount = txnsByAccountId.get(item.coa.id) || new List([]);
        const transferTxns = txnsFromTransferAccount.filter((tx) => isTransferTxn(tx));
        const oldTxn = findMatchingTransferTx(txnPrev, transferTxns, item.amount);
        oldTxn && oldDestinationTransfers.push(oldTxn);
      }
    });
  } else if (txnPrev.coa.type === 'ACCOUNT') {
    const txnsFromTransferAccount = txnsByAccountId.get(txnPrev.coa.id) || new List([]);
    const transferTxns = txnsFromTransferAccount.filter((tx) => isTransferTxn(tx));
    const oldTxn = findMatchingTransferTx(txnPrev, transferTxns);
    oldTxn && oldDestinationTransfers.push(oldTxn);
  }
  return oldDestinationTransfers;
}

//-------------------------------------------------------
// validateTxn
//
function validateTxn(txn) {

  if (!txn.postedOn) {
    return 'Must have a valid date';
  }

  if (!isSplitTxn(txn) && (!txn.coa || !txn.coa.type)) {
    return 'Please enter a Category/Transfer for this transaction';
  }

  // if (txn.amount === null || String(txn.amount).trim() === '') {
  //   return 'Please specify an amount';
  // }
  return null;
}


